import {
  ARENA_CX,
  ARENA_CY,
  GUEST_SPAWN,
  HOST_SPAWN,
  type DuelOutcome,
  type Role,
} from "@duelgame/shared";
import type { ArenaProp } from "./decor";

export type Phase =
  | "waiting"
  | "settle"
  | "maneuver"
  | "aim"
  | "result";

export interface Fighter {
  x: number;
  y: number;
  facing: number;
  /** Maneuver preview marker (next pace toward mouse). */
  targetX: number | null;
  targetY: number | null;
  /** Maneuver step destination currently being lerped toward. */
  moveToX: number | null;
  moveToY: number | null;
  /** Distance of the current pace lerp (for step anim progress). */
  stepDist: number;
  /** How many paces taken this maneuver (odd→step1, even→step2). */
  stepCount: number;
  /** 0–1 through the current step strip; 0 when idle. */
  stepProgress: number;
  /** Mirror sprite horizontally when true. */
  facingLeft: boolean;
  hasShot: boolean;
  hit: boolean;
  /** Current aim half-angle (radians). Cone width = 2 * spreadHalf. */
  spreadHalf: number;
  /** Smoothed pose for rendering (remote is lerped toward net state). */
  drawX: number;
  drawY: number;
  drawFacing: number;
  drawSpreadHalf: number;
  drawFacingLeft: boolean;
  drawStepCount: number;
  drawStepProgress: number;
  /** Active speech bubble, if any. */
  bubbleText: string | null;
  bubbleUntilMs: number;
  /** Fixed bubble anchor while fog is active; otherwise follows the fighter. */
  bubbleX: number | null;
  bubbleY: number | null;
}

export interface Bullet {
  id: number;
  owner: Role;
  x: number;
  y: number;
  vx: number;
  vy: number;
  alive: boolean;
  /** Sawed-off pellet — slower, shorter tracer. */
  shotgun: boolean;
}

/** Inflatable decoy dropped during the countdown. */
export interface CowboyDummy {
  owner: Role;
  x: number;
  y: number;
  facingLeft: boolean;
  /** Local clock when placed (drives inflate anim). */
  placedAtMs: number;
}

export interface DuelState {
  phase: Phase;
  localReady: boolean;
  remoteReady: boolean;
  /** Local clock: match start */
  matchStartMs: number | null;
  settleEndMs: number | null;
  maneuverEndMs: number | null;
  aimEndMs: number | null;
  countdownDisplay: number | null;
  fogActive: boolean;
  host: Fighter;
  guest: Fighter;
  bullets: Bullet[];
  /** Cowboy Dummy decoys placed this round. */
  dummies: CowboyDummy[];
  outcome: DuelOutcome | null;
  mouseX: number;
  mouseY: number;
  /** Synced arena decor (from match_start seed). */
  decor: ArenaProp[];
  /** Dark purple nighttime lighting for this round. */
  isNight: boolean;
  /** One-shot horse taunt when the countdown ends. */
  horseBubbleText: string | null;
  horseBubbleUntilMs: number;
  horseBubbleX: number;
  horseBubbleY: number;
}

function fighterFromSpawn(spawn: { x: number; y: number; facing: number }): Fighter {
  const facingLeft = Math.cos(spawn.facing) < 0;
  return {
    x: spawn.x,
    y: spawn.y,
    facing: spawn.facing,
    targetX: null,
    targetY: null,
    moveToX: null,
    moveToY: null,
    stepDist: 0,
    stepCount: 0,
    stepProgress: 0,
    facingLeft,
    hasShot: false,
    hit: false,
    spreadHalf: 0,
    drawX: spawn.x,
    drawY: spawn.y,
    drawFacing: spawn.facing,
    drawSpreadHalf: 0,
    drawFacingLeft: facingLeft,
    drawStepCount: 0,
    drawStepProgress: 0,
    bubbleText: null,
    bubbleUntilMs: 0,
    bubbleX: null,
    bubbleY: null,
  };
}

export function createInitialDuelState(): DuelState {
  return {
    phase: "waiting",
    localReady: false,
    remoteReady: false,
    matchStartMs: null,
    settleEndMs: null,
    maneuverEndMs: null,
    aimEndMs: null,
    countdownDisplay: null,
    fogActive: false,
    host: fighterFromSpawn(HOST_SPAWN),
    guest: fighterFromSpawn(GUEST_SPAWN),
    bullets: [],
    dummies: [],
    outcome: null,
    mouseX: ARENA_CX,
    mouseY: ARENA_CY,
    decor: [],
    isNight: false,
    horseBubbleText: null,
    horseBubbleUntilMs: 0,
    horseBubbleX: 0,
    horseBubbleY: 0,
  };
}

export function fighterFor(state: DuelState, role: Role): Fighter {
  return role === "host" ? state.host : state.guest;
}

export function otherRole(role: Role): Role {
  return role === "host" ? "guest" : "host";
}

/** Snap draw pose to simulation pose (local player). */
export function syncDrawPose(f: Fighter): void {
  f.drawX = f.x;
  f.drawY = f.y;
  f.drawFacing = f.facing;
  f.drawSpreadHalf = f.spreadHalf;
  f.drawFacingLeft = f.facingLeft;
  f.drawStepCount = f.stepCount;
  f.drawStepProgress = f.stepProgress;
}

/** Exponentially smooth draw pose toward net/sim pose (remote player). */
export function lerpDrawPose(f: Fighter, dt: number, rate = 16): void {
  const a = 1 - Math.exp(-Math.max(0, dt) * rate);
  f.drawX += (f.x - f.drawX) * a;
  f.drawY += (f.y - f.drawY) * a;
  f.drawFacing = lerpAngle(f.drawFacing, f.facing, a);
  f.drawSpreadHalf += (f.spreadHalf - f.drawSpreadHalf) * a;
  f.drawFacingLeft = f.facingLeft;
  f.drawStepCount = f.stepCount;
  f.drawStepProgress += (f.stepProgress - f.drawStepProgress) * a;
}

function lerpAngle(from: number, to: number, t: number): number {
  let diff = to - from;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return from + diff * t;
}
